import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Checkbox,
  Tooltip,
} from "antd";
import {
  LinkOutlined,
  SyncOutlined,
  DisconnectOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { useAgentProfile } from "../../hooks/useAgentProfile";
import { AgentMasterBulkUpdateModal } from "../../components/agent/AgentMasterBulkUpdateModal";
import {
  addContainerToMaster,
  createMasterContainer,
  deleteMasterContainer,
  fetchMasterContainers,
  getAvailableContainers,
  removeContainerFromMaster,
  updateMasterContainer,
} from "../../services/masterContainerService";
import type { MasterContainerWithContainers } from "../../types/booking";
import { formatDate } from "../../utils/bookingUtils";
import { getMasterTransportTypeName } from "../../utils/masterContainerTypeUtils";
import {
  formatContainerStatusLabel,
  getContainerStatusColor,
} from "../../utils/containerStatusColors";
import { MasterContainersNestedExpand } from "../../components/containers/MasterContainersNestedExpand";
import { AGENT_ROUTES } from "../../config/agentPortal";
import { AGENT_TABLE_SCROLL_X } from "../../config/agentPortalLayout";
import { AgentPageHeader } from "../../components/agent/AgentPageHeader";
import { AgentListTabs } from "../../components/agent/AgentListTabs";
import {
  AGENT_MASTER_STATUS_TABS,
  AgentMasterContainersFilters,
} from "../../components/agent/AgentMasterContainersFilters";
import {
  agentCompactTableEvenClass,
  spreadColumnsEvenly,
} from "../../components/agent/agentCompactTable";
import { MasterContainerAwbBlSection } from "../../components/containers/MasterContainerAwbBlSection";
import { MasterContainerPortFields } from "../../components/containers/MasterContainerPortFields";
import { getMasterContainerIdsWithDocuments } from "../../services/masterContainerDocumentsService";
import { useOriginPorts } from "../../hooks/useOriginPorts";
import { useDestinationPorts } from "../../hooks/useDestinationPorts";

const { Text } = Typography;

const AgentMasterContainersPage: React.FC = () => {
  const { agentId, agent, loading: profileLoading } = useAgentProfile();
  const { originPorts } = useOriginPorts();
  const { destinationPorts } = useDestinationPorts();
  const [searchParams, setSearchParams] = useSearchParams();
  const [masters, setMasters] = useState<MasterContainerWithContainers[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [transportFilter, setTransportFilter] = useState<number | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [manageMaster, setManageMaster] =
    useState<MasterContainerWithContainers | null>(null);
  const [bulkMaster, setBulkMaster] =
    useState<MasterContainerWithContainers | null>(null);
  const [availableContainers, setAvailableContainers] = useState<
    Array<{
      id: number;
      reference: string | null;
      status: string | null;
      transport_type?: number | null;
    }>
  >([]);
  const [createForm] = Form.useForm();
  const [manageForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [transportTypes, setTransportTypes] = useState<
    { id: number; name: string }[]
  >([]);
  const [uploadedMasterIds, setUploadedMasterIds] = useState<Set<number>>(
    new Set()
  );

  const refreshUploadStatus = useCallback(async (masterIds: number[]) => {
    const result = await getMasterContainerIdsWithDocuments(masterIds);
    if (result.success) {
      setUploadedMasterIds(result.data);
    }
  }, []);

  const load = useCallback(async () => {
    if (!agentId) return;
    setLoading(true);
    const result = await fetchMasterContainers({ agentId });
    if (result.error) {
      message.error("Failed to load Master BL/AWB list");
      setMasters([]);
      setUploadedMasterIds(new Set());
    } else {
      setMasters(result.data);
      await refreshUploadStatus(result.data.map((m) => m.id));
    }
    setLoading(false);
  }, [agentId, refreshUploadStatus]);

  useEffect(() => {
    if (!profileLoading) load();
  }, [profileLoading, load]);

  useEffect(() => {
    supabase
      .from("booking_transport_types")
      .select("id, name")
      .order("name")
      .then(({ data }) => setTransportTypes(data || []));
  }, []);

  const openManage = useCallback(
    async (master: MasterContainerWithContainers) => {
      setManageMaster(master);
      manageForm.setFieldsValue({
        reference: master.reference || "",
        origin_port: master.origin_port || undefined,
        destination_port: master.destination_port || undefined,
        container_id: undefined,
      });
      const result = await getAvailableContainers({
        transportTypeId: master.transport_type,
      });
      if (result.error) {
        message.error("Failed to load available containers");
        setAvailableContainers([]);
      } else {
        setAvailableContainers(result.data);
      }
    },
    [manageForm]
  );

  useEffect(() => {
    const masterId = searchParams.get("master_id");
    const action = searchParams.get("action");
    if (!masterId || action !== "manage" || masters.length === 0) return;
    const master = masters.find((m) => m.id === Number(masterId));
    if (!master) return;
    openManage(master);
    setSearchParams({}, { replace: true });
  }, [masters, searchParams, setSearchParams, openManage]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: masters.length };
    for (const tab of AGENT_MASTER_STATUS_TABS) {
      if (tab.key !== "all") counts[tab.key] = 0;
    }
    for (const master of masters) {
      if (master.status && counts[master.status] != null) {
        counts[master.status] += 1;
      }
    }
    return counts;
  }, [masters]);

  const hasActiveFilters =
    !!search.trim() || transportFilter != null;

  const clearFilters = () => {
    setSearch("");
    setTransportFilter(undefined);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return masters.filter((master) => {
      if (statusFilter !== "all" && master.status !== statusFilter) {
        return false;
      }
      if (
        transportFilter != null &&
        master.transport_type !== transportFilter
      ) {
        return false;
      }
      if (!q) return true;
      const transportName = getMasterTransportTypeName(master).toLowerCase();
      return (
        master.reference?.toLowerCase().includes(q) ||
        master.id.toString().includes(q) ||
        transportName.includes(q)
      );
    });
  }, [masters, search, statusFilter, transportFilter]);

  const handleCreate = async () => {
    if (!agentId) {
      message.error("Agent profile not loaded");
      return;
    }
    try {
      const values = await createForm.validateFields();
      setSubmitting(true);
      // Always assign to the logged-in agent (agents cannot change this)
      const result = await createMasterContainer({
        reference: values.reference.trim(),
        transport_type: values.transport_type,
        agent_id: agentId,
        status: values.status || "planned",
        track_and_trace: values.track_and_trace || null,
        origin_port: values.origin_port ?? null,
        destination_port: values.destination_port ?? null,
        eta: values.eta ? dayjs(values.eta).format("YYYY-MM-DD") : null,
        etd: values.etd ? dayjs(values.etd).format("YYYY-MM-DD") : null,
        delayed_etd: values.delayed_etd
          ? dayjs(values.delayed_etd).format("YYYY-MM-DD")
          : null,
        eta_customer: values.eta_customer
          ? dayjs(values.eta_customer).format("YYYY-MM-DD")
          : null,
      });
      if (result.error || !result.data) {
        message.error("Failed to create Master BL/AWB");
        return;
      }
      message.success("Master BL/AWB created and assigned to you");
      setCreateOpen(false);
      createForm.resetFields();
      load();
    } catch (error) {
      console.error(error);
      message.error("Failed to create Master BL/AWB");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateReference = async () => {
    if (!manageMaster) return;
    try {
      const values = await manageForm.validateFields([
        "reference",
        "origin_port",
        "destination_port",
      ]);
      setSubmitting(true);
      const originPort = values.origin_port ?? null;
      const destinationPort = values.destination_port ?? null;
      const result = await updateMasterContainer(manageMaster.id, {
        reference: values.reference.trim(),
        origin_port: originPort,
        destination_port: destinationPort,
      });
      if (result.error) {
        message.error("Failed to update Master BL/AWB");
        return;
      }
      message.success("Master BL/AWB updated");
      setManageMaster({
        ...manageMaster,
        reference: values.reference.trim(),
        origin_port: originPort,
        destination_port: destinationPort,
      });
      load();
    } catch (error) {
      console.error(error);
      message.error("Failed to update Master BL/AWB");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddContainer = async () => {
    if (!manageMaster) return;
    try {
      const values = await manageForm.validateFields(["container_id"]);
      setSubmitting(true);
      const result = await addContainerToMaster(
        manageMaster.id,
        values.container_id
      );
      if (!result.success) {
        message.error(
          (result.error as Error)?.message || "Failed to add container"
        );
        return;
      }
      message.success("Container added");
      manageForm.setFieldValue("container_id", undefined);
      await load();
      const refreshed = (
        await fetchMasterContainers({ agentId: agentId! })
      ).data.find((m) => m.id === manageMaster.id);
      if (refreshed) setManageMaster(refreshed);
      const available = await getAvailableContainers({
        transportTypeId: manageMaster.transport_type,
      });
      setAvailableContainers(available.data);
    } catch (error) {
      console.error(error);
      message.error("Failed to add container");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveContainer = async (containerId: number) => {
    if (!manageMaster || !agentId) return;
    setSubmitting(true);
    const result = await removeContainerFromMaster(
      manageMaster.id,
      containerId
    );
    if (!result.success) {
      message.error("Failed to remove container");
      setSubmitting(false);
      return;
    }
    message.success("Container removed");
    await load();
    const refreshed = (
      await fetchMasterContainers({ agentId })
    ).data.find((m) => m.id === manageMaster.id);
    if (refreshed) setManageMaster(refreshed);
    const available = await getAvailableContainers({
      transportTypeId: manageMaster.transport_type,
    });
    setAvailableContainers(available.data);
    setSubmitting(false);
  };

  const handleDelete = async (id: number) => {
    const result = await deleteMasterContainer(id);
    if (!result.success) {
      message.error("Failed to delete Master BL/AWB");
      return;
    }
    message.success("Master BL/AWB deleted");
    load();
  };

  const confirmDelete = (master: MasterContainerWithContainers) => {
    const label = master.reference || `Master #${master.id}`;
    Modal.confirm({
      title: "Delete this Master BL/AWB?",
      content: (
        <div>
          <p style={{ marginBottom: 8 }}>
            You are about to delete <strong>{label}</strong>.
          </p>
          <p style={{ marginBottom: 0 }}>
            Container links will be removed. This cannot be undone.
          </p>
        </div>
      ),
      okText: "Continue",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        Modal.confirm({
          title: "Are you sure?",
          content: (
            <div>
              <p style={{ marginBottom: 8 }}>
                Final confirmation: permanently delete{" "}
                <strong>{label}</strong>?
              </p>
              <p style={{ marginBottom: 0 }}>
                Linked containers will be unlinked from this Master BL/AWB.
              </p>
            </div>
          ),
          okText: "Yes, delete permanently",
          okType: "danger",
          cancelText: "Cancel",
          onOk: () => handleDelete(master.id),
        });
      },
    });
  };

  const columns = spreadColumnsEvenly<MasterContainerWithContainers>(
    [
      {
        title: "Reference",
        dataIndex: "reference",
        render: (value) => value || "—",
      },
      {
        title: "Transport",
        key: "transport_type",
        render: (_, record) => getMasterTransportTypeName(record),
      },
      {
        title: "Origin",
        key: "origin_port",
        render: (_, record) => {
          const info = Array.isArray(record.origin_port_info)
            ? record.origin_port_info[0]
            : record.origin_port_info;
          return info?.name || "—";
        },
      },
      {
        title: "Destination",
        key: "destination_port",
        render: (_, record) => {
          const info = Array.isArray(record.destination_port_info)
            ? record.destination_port_info[0]
            : record.destination_port_info;
          return info?.name || "—";
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        render: (status: string | null) =>
          status ? (
            <Tag color={getContainerStatusColor(status)}>
              {formatContainerStatusLabel(status)}
            </Tag>
          ) : (
            "—"
          ),
      },
      {
        title: "Containers",
        dataIndex: "container_count",
        render: (count: number) => (
          <Tag color={count > 0 ? "blue" : "default"}>
            {count} container{count === 1 ? "" : "s"}
          </Tag>
        ),
      },
      {
        title: "AWB/BL",
        key: "awb_bl_uploaded",
        align: "center" as const,
        render: (_: unknown, record) => {
          const uploaded = uploadedMasterIds.has(record.id);
          return (
            <Tooltip title={uploaded ? "Master bill uploaded" : "Not uploaded yet"}>
              <Checkbox checked={uploaded} disabled />
            </Tooltip>
          );
        },
      },
      {
        title: "ETD",
        dataIndex: "etd",
        render: (_date, record) => {
          const effective = record.delayed_etd || record.etd;
          return formatDate(effective) || "—";
        },
        defaultSortOrder: "ascend",
        sorter: (a, b) => {
          const dateA = a.delayed_etd || a.etd;
          const dateB = b.delayed_etd || b.etd;
          const timeA = dateA ? new Date(dateA).getTime() : 0;
          const timeB = dateB ? new Date(dateB).getTime() : 0;
          return timeA - timeB;
        },
      },
      {
        title: "ETA",
        dataIndex: "eta",
        render: (date) => formatDate(date) || "—",
        sorter: (a, b) => {
          const dateA = a.eta ? new Date(a.eta).getTime() : 0;
          const dateB = b.eta ? new Date(b.eta).getTime() : 0;
          return dateA - dateB;
        },
      },
      {
        title: "Actions",
        key: "actions",
        ellipsis: false,
        render: (_, record) => (
          <Space size={0} style={{ whiteSpace: "nowrap" }}>
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => openManage(record)}
              style={{ paddingInline: 4 }}
            >
              Manage
            </Button>
            <Button
              type="link"
              size="small"
              icon={<SyncOutlined />}
              disabled={(record.container_count || 0) === 0}
              onClick={() => setBulkMaster(record)}
              style={{ paddingInline: 4 }}
            >
              Update
            </Button>
            <Button
              type="link"
              danger
              size="small"
              onClick={() => confirmDelete(record)}
              style={{ paddingInline: 4 }}
            >
              Delete
            </Button>
          </Space>
        ),
      },
    ],
    // Reference, Transport, Status, Containers, AWB/BL, ETD, ETA, Actions
    [1.4, 1, 1, 1.2, 0.7, 1.2, 1.2, 2]
  );

  return (
    <div>
      <AgentPageHeader title="Master BL/AWB" agentName={agent?.name} />

      <AgentListTabs
        activeKey={statusFilter}
        onChange={setStatusFilter}
        items={AGENT_MASTER_STATUS_TABS.map((tab) => ({
          key: tab.key,
          label: tab.label,
          count: statusCounts[tab.key] ?? 0,
        }))}
      />

      <AgentMasterContainersFilters
        search={search}
        transportFilter={transportFilter}
        transportTypes={transportTypes}
        hasActiveFilters={hasActiveFilters}
        resultCount={filtered.length}
        onSearchChange={setSearch}
        onTransportChange={setTransportFilter}
        onClearFilters={clearFilters}
        onRefresh={load}
        onAdd={() => {
          createForm.resetFields();
          createForm.setFieldsValue({ status: "planned" });
          setCreateOpen(true);
        }}
      />

      <Table
        rowKey="id"
        size="small"
        loading={loading || profileLoading}
        dataSource={filtered}
        columns={columns}
        tableLayout="fixed"
        className={agentCompactTableEvenClass}
        scroll={{ x: AGENT_TABLE_SCROLL_X }}
        pagination={{
          pageSize: 100,
          showSizeChanger: true,
          responsive: true,
          showTotal: (total) => `${total} masters`,
        }}
        expandable={{
          expandedRowRender: (record) => (
            <MasterContainersNestedExpand
              containers={record.containers || []}
              getBookingPath={AGENT_ROUTES.bookingDetail}
              getContainerPath={AGENT_ROUTES.containerDetail}
            />
          ),
          rowExpandable: (record) => (record.containers?.length || 0) > 0,
        }}
      />

      <Modal
        title="Add Master BL/AWB"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="reference"
            label="Reference"
            rules={[{ required: true, message: "Reference is required" }]}
          >
            <Input placeholder="Master BL or AWB reference" />
          </Form.Item>
          <Form.Item
            name="transport_type"
            label="Transport type"
            rules={[{ required: true, message: "Select transport type" }]}
          >
            <Select
              options={transportTypes.map((t) => ({
                value: t.id,
                label: t.name,
              }))}
            />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select
              allowClear
              options={[
                { value: "planned", label: "Planned" },
                { value: "departed", label: "Departed" },
                { value: "arrived", label: "Arrived" },
              ]}
            />
          </Form.Item>
          <Form.Item name="track_and_trace" label="Track & Trace">
            <Input placeholder="Optional track & trace URL" />
          </Form.Item>
          <MasterContainerPortFields
            originPorts={originPorts}
            destinationPorts={destinationPorts}
            required
          />
        </Form>
      </Modal>

      <Modal
        title={`Manage containers — ${manageMaster?.reference || "Master BL/AWB"}`}
        open={!!manageMaster}
        onCancel={() => {
          setManageMaster(null);
          manageForm.resetFields();
        }}
        footer={null}
        width={720}
        destroyOnClose
      >
        {manageMaster && (
          <>
            <Space style={{ marginBottom: 12 }}>
              <Tag color="blue">
                {getMasterTransportTypeName(manageMaster)}
              </Tag>
              <Text type="secondary">
                Only matching {getMasterTransportTypeName(manageMaster)}{" "}
                containers can be linked.
              </Text>
            </Space>

            <Form form={manageForm} layout="vertical">
              <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
                <Form.Item
                  name="reference"
                  style={{ flex: 1, marginBottom: 0 }}
                  rules={[{ required: true, message: "Reference is required" }]}
                >
                  <Input placeholder="Master reference" />
                </Form.Item>
                <Button
                  type="primary"
                  loading={submitting}
                  onClick={handleUpdateReference}
                >
                  Update details
                </Button>
              </Space.Compact>

              <MasterContainerPortFields
                originPorts={originPorts}
                destinationPorts={destinationPorts}
                required
              />

              <Space.Compact style={{ width: "100%", marginBottom: 16 }}>
                <Form.Item
                  name="container_id"
                  style={{ flex: 1, marginBottom: 0 }}
                  rules={[{ required: true, message: "Select a container" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select container to add"
                    options={availableContainers.map((c) => ({
                      value: c.id,
                      label: `${c.reference || `Container ${c.id}`} — ${c.status || "no status"}`,
                    }))}
                  />
                </Form.Item>
                <Button
                  type="primary"
                  icon={<LinkOutlined />}
                  loading={submitting}
                  onClick={handleAddContainer}
                >
                  Add
                </Button>
              </Space.Compact>
            </Form>

            <MasterContainerAwbBlSection
              masterContainerId={manageMaster.id}
              masterReference={manageMaster.reference}
              style={{ marginBottom: 16 }}
              onDocumentChange={() => {
                void refreshUploadStatus(masters.map((m) => m.id));
              }}
            />

            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={manageMaster.containers || []}
              locale={{ emptyText: "No containers linked yet" }}
              columns={[
                {
                  title: "Reference",
                  dataIndex: "reference",
                  render: (v) => v || "—",
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (status: string) => (
                    <Tag color={getContainerStatusColor(status)}>
                      {formatContainerStatusLabel(status)}
                    </Tag>
                  ),
                },
                {
                  title: "Actions",
                  key: "actions",
                  render: (_, container) => (
                    <Popconfirm
                      title="Remove this container?"
                      onConfirm={() => handleRemoveContainer(container.id)}
                    >
                      <Button
                        type="link"
                        danger
                        size="small"
                        icon={<DisconnectOutlined />}
                      >
                        Remove
                      </Button>
                    </Popconfirm>
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>

      <AgentMasterBulkUpdateModal
        master={bulkMaster}
        open={!!bulkMaster}
        onClose={() => setBulkMaster(null)}
        onSuccess={load}
      />
    </div>
  );
};

export default AgentMasterContainersPage;
